<script>
    import { slide } from 'svelte/transition';
    import Spinner from 'svelte-spinner';
    import { Tooltip } from "sveltestrap";

    import { vptServer, token, selectedInmate, selectedInmateDateArrived, selectedInmateDateDeparted } from '../stores.js';
    import { formatDate, formatDateTime, getIsoDateStringAsDate, isToday } from '../date-time.js';
    import { referenceDataEthnicityList, referenceDataRegimeLevelList, referenceDataCellStatusList, getReferenceDataSiteName, getReferenceDataItemFromList } from "../reference-data";

    export let id;
    export let todaysConfig;
    export let selectedInmateAspect;

    let inmate;
    let images;
    let firstPage = true;
    let displayedImage;
    let displayedImageIndex;
    let imageSelected = false;

    $: {
        if (todaysConfig) {
            inmate = null;
            images = null;
            getPersonalDetails();
            getImages();
        }
    }

    async function getPersonalDetails() {
        await fetch(`${$vptServer}/inmate/${id}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            inmate = data;
            selectedInmate.set(inmate.inmateName);
            selectedInmateDateArrived.set(inmate.dateArrived);
            selectedInmateDateDeparted.set(inmate.dateDeparted);
        })
        .catch(error => {
            console.error(error);
            return;
        });
    }

    async function getImages() {
        await fetch(`${$vptServer}/inmate/${id}/images`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            images = data;
            images.forEach((i,index) => {
               if (i.primaryFlag) {
                   displayedImage = i.image;
                   displayedImageIndex = index;
               };
            });
        })
        .catch(error => {
            
            console.error("Error getting inmate summary");
            console.error(error);
            return;
        });
    }

    const displayPreviousImage = () => {
        if (displayedImageIndex > 0) {
            displayedImageIndex--;
        }
        displayedImage = images[displayedImageIndex].image;
    };

    const displayNextImage = () => {
        if (displayedImageIndex < (images.length - 1)) {
            displayedImageIndex++;
        }
        displayedImage = images[displayedImageIndex].image;
    };
</script>

<style>
    .inmate-widget {
        width: 440px;
        margin-left: 15px;
    }

    .inmate-widget .widget-content {
        padding: 0 0 0 6px;
    }

    .carousel-widget {
        flex-shrink: 0;
        height: 124px;
        width: 110px;
    }

    .carousel-widget:focus-visible img {
        border-radius: 5px;
    }

    .carousel-inner, .carousel-item, .inmate-image {
        height: 100%;   
        width: 100%;
    }

    .inmate-image {
        object-fit: cover;
    }

    .carousel-control-next, .carousel-control-prev {
        opacity: .75;
        width: 25%;
        align-items: flex-end;
        padding-bottom: 8px;
    }

    .carousel-control-next-icon, .carousel-control-prev-icon {
        width: 22px;
        height: 12%;
    }

    #image-placeholder {
        margin-right: 10px;
    }

    #image-placeholder img {
        height: 90px;
        margin: 10px;
    }

    .card-details {
        height: 124px;
        display: flex;
        flex-direction: column;
        flex-grow: 100;
    }

    .card-detail-page {
        display: flex;
        flex-direction: row;
    }

    .card-flex-column {
        white-space: nowrap;
        height: 92px;
        justify-content: space-evenly;
    }

    .card-detail {
        padding-left: 10px;
    }

    .card-detail-header {
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
    }

    .card-detail-header img {
        padding-bottom: 2px;
    }

    #birthday-indicator {
        padding-bottom: 2px;
        padding-left: 2px;
    }

    #arrival-row {
        display: flex;
        align-items: center;
    }

    .badge-info {
        margin: 0 4px 0 0;
    }
    
    .badge-arrival {
        margin: 0 0 0 4px;
    }

    .card-page-control {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .card-page-control button {
        line-height: 12px;
        padding: 0 4px;
    }

    .card-page-control button:first-of-type {
        padding-left: 20px;
    }

    .card-page-control button:last-of-type {
        padding-right: 20px;
    }

    .card-page-control button img {
        height: 10px;
    }

    #image-popup {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.7);
        z-index: 1031;
    }

    #image-popup-content {
        padding-top: 120px;
        z-index: 1032;
        display: flex;
        justify-content: center;
    }

    #image-popup-img-wrapper {
        position: relative;
    }

    #image-popup-img {
        max-height: 500px;
        max-width: 500px;
    } 
    
    #image-popup-close {
        color: white;
        background: rgba(0, 0, 0, 0.7);
        position: absolute;
        right: 0;
        font-size: 24px;
        padding: 0 10px;
    }

    #image-popup .carousel-control-next, #image-popup .carousel-control-prev {
        top: 30px;
        width: 20%;
    }

    #inmate-cell-status-indicator {
        padding-bottom: 2px;
    }
</style>

<div class="inmate-widget">
    {#if inmate}
        <button class="button-reset focus-outline card" on:click class:selected={selectedInmateAspect === "Overall"}>
            <div class="card-body">
                <div class="widget-container">
                    <div class="widget-content">
                        {#if images && images.length > 0}
                            <button on:click|stopPropagation="{() => imageSelected = true}" type="button" class="carousel-widget carousel button-reset focus-outline">
                                <div class="carousel-inner">
                                    <div class="carousel-item active">
                                        <img class="inmate-image" src="data:image/jpg;base64,{displayedImage}" alt="inmate" />
                                    </div>
                                </div>

                                {#if displayedImageIndex > 0}
                                    <a on:click|stopPropagation="{displayPreviousImage}" class="carousel-control-prev focus-outline"
                                            href="#carouselExampleControls" role="button">
                                        <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                                        <span class="sr-only">Previous</span>
                                    </a>
                                {/if}

                                {#if displayedImageIndex < (images.length - 1)}
                                    <a on:click|stopPropagation="{displayNextImage}" class="carousel-control-next focus-outline"
                                            href="#carouselExampleControls" role="button">
                                        <span class="carousel-control-next-icon" aria-hidden="true"></span>
                                        <span class="sr-only">Next</span>
                                    </a>
                                {/if}
                            </button>
                        {:else}
                            <div id="image-placeholder">
                                <img src="/assets/person.svg" alt="no images" />
                            </div>
                        {/if}
                                
                        <div class="card-details">

                            <span class="card-detail card-detail-header">
                                {#if inmate.targetedTotal > 0}
                                    <img id="inmate-targeted-multiple-{inmate.inmateUrn}" src="/assets/target-indicator{inmate.targetedTotal > 1 ? '-danger' : ''}.svg" alt="Targeted on {inmate.targetedTotal} aspects" />
                                    <Tooltip target="inmate-targeted-multiple-{inmate.inmateUrn}" placement="bottom">Targeted on {inmate.targetedTotal} aspect{inmate.targetedTotal > 1 ? 's' : ''}</Tooltip>
                                {/if}

                                <span id="inmate-pnc-dob">
                                    {inmate.inmateName} 
                                    {#if inmate.dob && inmate.age}
                                        {#if inmate.age.includes('Today')}
                                            ({inmate.age}<img id="birthday-indicator" src="/assets/cake.svg" alt="Today is their birthday" />)
                                        {:else}
                                            ({inmate.age})
                                        {/if}
                                    {/if}
                                </span>
                                <Tooltip target="inmate-pnc-dob" placement="bottom">
                                    {inmate.prisonNumber.toUpperCase()} {#if inmate.dob} &middot; {formatDate(inmate.dob)}{/if}
                                    {#if inmate.age.includes('Today')}&nbsp;&nbsp;&nbsp;Today is their birthday&nbsp;&nbsp;&nbsp;{/if}
                                </Tooltip>
                            </span>

                            <div class="card-detail-page">          
                                {#if firstPage}
                                    <div transition:slide|local={{ duration: 300, axis: 'x' }} class="card-flex-column">

                                        <span class="card-detail">
                                            {#if inmate.indicatorRemand}
                                                <img class="remand-indicator" src="/assets/hourglass-indicator.svg" alt="{todaysConfig.inmateLabelName} is on remand" />
                                            {/if}
                                            {#if inmate.status}
                                                {inmate.status}
                                            {:else}
                                                Status Not Recorded
                                            {/if}
                                        </span>

                                        <span id="arrival-row" class="card-detail">
                                            {#if inmate.indicatorLiveInmate}
                                                Arrived {formatDate(inmate.dateArrived)}
                                                {#if inmate.indicatorNewInmate}
                                                    <span id="inmate-arrival-new" class="badge badge-danger badge-arrival badge-border">{inmate.timeSinceArrival}</span>
                                                    <Tooltip target="inmate-arrival-new" placement="bottom">
                                                        Within New Arrival Period
                                                    </Tooltip>
                                                {:else if inmate.indicatorEarlyDays}
                                                    <span id="inmate-arrival-early-days" class="badge badge-primary badge-arrival">{inmate.timeSinceArrival}</span>
                                                    <Tooltip target="inmate-arrival-early-days" placement="bottom">
                                                        Within Early Days Period
                                                    </Tooltip>
                                                {:else}
                                                    ({inmate.timeSinceArrival})
                                                {/if}
                                            {:else}
                                                {#if inmate.dateTemporaryDischarged}
                                                    <span id="inmate-temporary-discharge" class="badge badge-info">Temporarily Discharged</span>
                                                    <Tooltip target="inmate-temporary-discharge" placement="bottom">
                                                        {inmate.dischargedTo}
                                                    </Tooltip>
                                                    {#if isToday(getIsoDateStringAsDate(inmate.dateTemporaryDischarged))}
                                                        {formatDateTime(inmate.dateTemporaryDischarged)}
                                                    {:else}
                                                        {formatDate(inmate.dateTemporaryDischarged)}
                                                    {/if}
                                                {:else}
                                                    <span class="badge badge-info">Discharged</span>
                                                    {#if inmate.dateDeparted}
                                                        {formatDate(inmate.dateArrived)} - {formatDate(inmate.dateDeparted)}
                                                    {:else}
                                                        Arrived {formatDate(inmate.dateArrived)}
                                                    {/if}
                                                {/if}
                                            {/if}
                                        </span>

                                        <span class="card-detail">
                                            {#if todaysConfig.showSiteName && inmate.siteUrn}
                                                {getReferenceDataSiteName(inmate.siteUrn)} &gt;
                                            {/if}

                                            {#if inmate.unit || inmate.wing || inmate.cell}
                                                {#if inmate.unit}{inmate.unit}{/if}
                                                {#if inmate.unit && inmate.wing}&nbsp;&middot;&nbsp;{/if}
                                                {#if inmate.wing}{inmate.wing}{/if}
                                                {#if (inmate.unit || inmate.wing) && inmate.cell}&nbsp;&middot;&nbsp;{/if}
                                                {#if inmate.cell}{inmate.cell}{/if}
                                            {:else}
                                                Location Not Recorded
                                            {/if}

                                            {#if inmate.vptCellStatusUrn > 0}
                                                <img id="inmate-cell-status-indicator" src="/assets/{getReferenceDataItemFromList(inmate.vptCellStatusUrn, $referenceDataCellStatusList, 'iconName', 'vptCellStatusUrn')}.svg" alt="{getReferenceDataItemFromList(inmate.vptCellStatusUrn, $referenceDataCellStatusList, 'description', 'vptCellStatusUrn')}" />
                                                <Tooltip target="inmate-cell-status-indicator" placement="bottom">
                                                    {getReferenceDataItemFromList(inmate.vptCellStatusUrn, $referenceDataCellStatusList, 'description', 'vptCellStatusUrn')}
                                                </Tooltip>
                                            {/if}
                                        </span>

                                        <span class="card-detail">
                                            {#if inmate.regimeLevel}
                                                {getReferenceDataItemFromList(inmate.regimeLevel, $referenceDataRegimeLevelList, 'description', 'level')} Regime (Since {formatDate(inmate.regimeStartDate)})
                                            {:else}
                                                Regime Not Recorded
                                            {/if}
                                        </span>
                                    </div>
                                {:else}
                                    <div transition:slide|local={{ duration: 300, axis: 'x' }} class="card-flex-column">      

                                        <span class="card-detail">
                                            {inmate.prisonNumber.toUpperCase()} &middot; {inmate.dob ? formatDate(inmate.dob) : 'Date of Birth Not Recorded'}
                                        </span>

                                        <span class="card-detail">
                                            {#if inmate.nationality}{inmate.nationality}{/if}
                                            {#if inmate.nationality && inmate.ethnicity}&nbsp;&middot;&nbsp;{/if}
                                            {#if inmate.ethnicity}{getReferenceDataItemFromList(inmate.ethnicity, $referenceDataEthnicityList,'description','code')}{/if}
                                        </span>

                                        <span class="card-detail">
                                            {#if inmate.religion}{inmate.religion}{/if}{#if inmate.religion && (inmate.religion.toLowerCase() == 'unknown' || inmate.religion.toLowerCase() == 'nil')}&nbsp;Religion{/if}
                                            {#if !inmate.religion}Religion Not Recorded{/if}
                                        </span>

                                        <span class="card-detail">
                                            {#if inmate.keyworkerName}
                                                <span id="card-detail-keyworker">{inmate.keyworkerName} (Since {formatDate(inmate.keyworkerStartDate)})</span>
                                                <Tooltip target="card-detail-keyworker" placement="bottom">{todaysConfig.keyworkerLabelName}</Tooltip>
                                            {:else}{todaysConfig.keyworkerLabelName} Not Recorded{/if}
                                        </span>
                                    </div>
                                {/if}
                            </div>
                            
                            <div class="card-page-control">
                                <button class="button-reset focus-outline" on:click|stopPropagation={() => {firstPage = true}}>
                                    {#if firstPage}
                                        <img src="/assets/circle-fill.svg" alt="first page" />
                                    {:else}
                                        <img src="/assets/circle.svg" alt="first page" />
                                    {/if}
                                </button>
                                <button class="button-reset focus-outline" on:click|stopPropagation={() => {firstPage = false}}>
                                    {#if !firstPage}
                                        <img src="/assets/circle-fill.svg" alt="second page" />
                                    {:else}
                                        <img src="/assets/circle.svg" alt="second page" />
                                    {/if}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </button>
    {:else}
        <div class="card" class:selected={selectedInmateAspect === "Isolation"}>
            <div class="card-body">
                <div class="widget-container">
                    <div class="widget-spinner">
                        <Spinner size="100" speed="750" color="#3d5980" thickness="3" gap="15" />
                    </div>
                </div>
            </div>
        </div>
    {/if}
</div>

{#if imageSelected}
    <div id="image-popup">
        <div id="image-popup-content">
            <div id="image-popup-img-wrapper">

                <button type="button" id="image-popup-close" class="button-reset focus-outline" aria-label="Close" 
                        on:click="{() => imageSelected = false}">
                    <span aria-hidden="true">&times;</span>
                </button>
                
                <img id="image-popup-img" src="data:image/jpg;base64,{displayedImage}" alt="inmate pop up">
                
                    {#if displayedImageIndex > 0}                   
                        <a on:click|stopPropagation="{displayPreviousImage}" class="carousel-control-prev focus-outline" href="#carouselExampleControls" role="button">
                            <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                            <span class="sr-only">Previous</span>
                        </a>
                    {/if}

                    {#if displayedImageIndex < (images.length - 1)}
                        <a on:click|stopPropagation="{displayNextImage}" class="carousel-control-next focus-outline" href="#carouselExampleControls" role="button">
                            <span class="carousel-control-next-icon" aria-hidden="true"></span>
                            <span class="sr-only">Next</span>
                        </a>
                    {/if}
            </div>
        </div>
    </div>
{/if}
